<template>
  <div class="tavern-account-container">
    <!-- Search and Action Section -->
    <div class="search-action">
      <el-input
          v-model="searchForm.account"
          placeholder="搜索账号"
          clearable
          @clear="handleSearch"
          @input="handleSearch"
      >
      </el-input>
      <div style="padding-left: 10px;padding-right: 10px">
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </div>
      <div style="padding-left: 10px;padding-right: 10px">
        <el-button type="success" size="small" @click="showAddDialog">新增账号</el-button>
      </div>
    </div>

    <!-- Table Section -->
    <el-table :data="tavernAccounts" border style="width: 100%">
      <el-table-column type="index" label="序号" width="50"></el-table-column>
      <el-table-column prop="account" label="账号" width="120"></el-table-column>
      <el-table-column label="密码" width="180">
        <template slot-scope="scope">
          <span>{{ hidePassword(scope.row.password) }}</span>
          <el-button
              size="mini"
              icon="el-icon-document-copy"
              @click="copyPassword(scope.row.password)"
          >复制</el-button>
        </template>
      </el-table-column>
      <el-table-column prop="registerDate" label="注册日期" width="160">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.registerDate) }}
        </template>
      </el-table-column>

      <el-table-column prop="expiryDate" label="到期日期" width="160">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.expiryDate) }}
        </template>
      </el-table-column>
      <el-table-column label="剩余天数" width="80">
        <template slot-scope="scope">
          {{ calculateRemainingDays(scope.row.expiryDate) }}
        </template>
      </el-table-column>
      <el-table-column prop="accountLevel" label="账号等级" width="80">
        <template slot-scope="scope">
          {{ scope.row.accountLevel }}
        </template>
      </el-table-column>
      <el-table-column prop="accountStatus" label="账号状态" width="80">
        <template slot-scope="scope">
          <span :class="getStatusClass(scope.row.accountStatus)">
            {{ ['正常', '封禁', '过期'][scope.row.accountStatus] }}
          </span>
        </template>
      </el-table-column>
      <!-- 在操作列中添加续费按钮 -->
      <el-table-column label="操作" width="280">
        <template slot-scope="scope">
          <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          <el-button
              size="mini"
              type="warning"
              @click="handleBan(scope.row)"
              v-if="scope.row.accountStatus === 0"
          >封禁</el-button>
          <!-- 新增续费按钮 -->
          <el-button
              size="mini"
              type="success"
              @click="handleRenew(scope.row)"
          >续费</el-button>
        </template>
      </el-table-column>

    </el-table>

    <!-- Pagination Section -->
    <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
    >
    </el-pagination>

    <!-- Add/Edit Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="30%" @close="resetForm">
      <el-form ref="form" :model="form" label-width="100px">
        <el-form-item label="账号" prop="account" :rules="[{ required: true, message: '请输入账号', trigger: 'blur' }]">
          <el-input v-model="form.account"></el-input>
        </el-form-item>
        <el-form-item label="密码" prop="password" :rules="[{ required: true, message: '请输入密码', trigger: 'blur' }]">
          <el-input v-model="form.password" type="password"></el-input>
        </el-form-item>
        <el-form-item label="注册日期" prop="registerDate" :rules="[{ required: true, message: '请选择注册日期', trigger: 'change' }]">
          <el-date-picker
              v-model="form.registerDate"
              type="datetime"
              placeholder="选择日期时间"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="账号状态" prop="accountStatus">
          <el-select v-model="form.accountStatus">
            <el-option :value="0" label="正常"></el-option>
            <el-option :value="1" label="封禁"></el-option>
            <el-option :value="2" label="过期"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  data() {
    return {
      statusCounts: [0, 0, 0],
      searchForm: {
        account: ''
      },
      tavernAccounts: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      dialogVisible: false,
      dialogTitle: '新增账号',
      form: {
        id: null,
        account: '',
        password: '',
        registerDate: '',
        lastLoginDate: '',
        expiryDate: '',
        accountLevel: 1,
        accountStatus: 0,
        vipLevel: 0
      }
    };
  },
  created() {
    this.setupAxiosInterceptors();
  },
  methods: {
    async handleRenew(row) {
      try {
        await this.$confirm('确定要为该账号续费30天吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        // 创建一个新的对象，只包含必要的字段
        const renewData = {
          id: row.id,
          registerDate: row.expiryDate // 使用当前到期时间作为新的注册时间
        };

        const response = await axios.post('/api/admin/saveOrUpdateAccount', renewData);

        if (response.data.code === 200) {
          this.$message.success('续费成功');
          await this.fetchTavernAccounts(); // 刷新表格数据
        } else {
          this.$message.error(response.data.msg || '续费失败');
        }
      } catch (error) {
        if (error === 'cancel') {
          return;
        }
        console.error('Error renewing account:', error);
        this.$message.error('续费失败');
      }
    },
    setupAxiosInterceptors() {
      axios.interceptors.request.use(
          config => {
            const token = this.$cookies.get('token');
            if (token) {
              config.headers['token'] = token;
            }
            return config;
          },
          error => {
            return Promise.reject(error);
          }
      );

      axios.interceptors.response.use(
          response => response,
          error => {
            if (error.response && error.response.status === 401) {
              this.$message.error('认证失败，请重新登录');
              this.$router.push('/login');
            }
            return Promise.reject(error);
          }
      );
    },
    async fetchTavernAccounts() {
      try {
        const response = await axios.post('/api/admin/account/page', {
          current: this.currentPage,
          size: this.pageSize,
          account: this.searchForm.account
        });
        if (response.data.code === 200) {
          this.tavernAccounts = response.data.data;
          this.total = response.data.total;
          this.currentPage = response.data.current;
          this.pageSize = response.data.size;
          this.calculateStatusCounts();
        } else {
          this.$message.error(response.data.msg || '获取账号记录失败');
        }
      } catch (error) {
        console.error('Error fetching tavern accounts:', error);
        this.$message.error('获取账号记录失败');
      }
    },
    calculateStatusCounts() {
      this.statusCounts = [0, 0, 0];
      this.tavernAccounts.forEach(account => {
        if (account.accountStatus >= 0 && account.accountStatus <= 2) {
          this.statusCounts[account.accountStatus]++;
        }
      });
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchTavernAccounts();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchTavernAccounts();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchTavernAccounts();
    },
    showAddDialog() {
      this.dialogTitle = '新增账号';
      this.dialogVisible = true;
      this.form = {
        id: null,
        account: '',
        password: '',
        registerDate: '',
        lastLoginDate: '',
        expiryDate: '',
        accountLevel: 1,
        accountStatus: 0,
        vipLevel: 0
      };
    },
    handleEdit(row) {
      this.dialogTitle = '编辑账号';
      this.dialogVisible = true;
      this.form = { ...row };
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除这个账号吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        const response = await axios.post('/api/admin/removeAccount', null, {
          params: { id: row.id }
        });
        if (response.data.code === 200) {
          this.$message.success('删除成功');
          this.fetchTavernAccounts();
        } else {
          this.$message.error(response.data.msg || '删除失败');
        }
      } catch (error) {
        console.error('Error deleting tavern account:', error);
        this.$message.error('删除失败');
      }
    },
    async handleBan(row) {
      try {
        await this.$confirm('确定要封禁这个账号吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        const response = await axios.post('/api/admin/banAccount', {
          id: row.id,
          accountStatus: 1
        });
        if (response.data.code === 200) {
          this.$message.success('封禁成功');
          this.fetchTavernAccounts();
        } else {
          this.$message.error(response.data.msg || '封禁失败');
        }
      } catch (error) {
        console.error('Error banning account:', error);
        this.$message.error('封禁失败');
      }
    },
    resetForm() {
      this.$refs.form.resetFields();
    },
    async submitForm() {
      try {
        await this.$refs.form.validate();
        const response = await axios.post('/api/admin/saveOrUpdateAccount', this.form);
        if (response.data.code === 200) {
          this.$message.success('操作成功');
          this.dialogVisible = false;
          this.fetchTavernAccounts();
        } else {
          this.$message.error(response.data.msg || '操作失败');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        this.$message.error('提交失败');
      }
    },
    formatDateTime(dateTime) {
      return dateTime ? moment(dateTime).format('YYYY-MM-DD HH:mm:ss') : '';
    },
    calculateRemainingDays(expiryDate) {
      if (!expiryDate) return '未设置';
      const now = moment();
      const expiry = moment(expiryDate);
      const diff = expiry.diff(now, 'days');
      return diff < 0 ? '已过期' : `${diff}天`;
    },
    getStatusClass(status) {
      switch (status) {
        case 0:
          return 'status-green';
        case 1:
          return 'status-red';
        case 2:
          return 'status-gray';
        default:
          return '';
      }
    },
    hidePassword(password) {
      return password ? '*'.repeat(password.length) : '';
    },
    copyPassword(password) {
      navigator.clipboard.writeText(password).then(() => {
        this.$message.success('密码已复制到剪贴板');
      }).catch(err => {
        console.error('Failed to copy password: ', err);
        this.$message.error('复制密码失败');
      });
    }
  },
  mounted() {
    this.fetchTavernAccounts();
  }
}
</script>

<style scoped>


.search-action {
  display: flex;
  width: 360px;
}

.status-counts {
  margin-left: auto;
  display: flex;
  gap: 10px;
}

.status-counts div {
  padding: 5px 10px;
  border-radius: 4px;
  background-color: #f5f7fa;
}

.status-green {
  color: #67C23A;
}

.status-red {
  color: #F56C6C;
}

.status-gray {
  color: #909399;
}

.el-pagination {
  margin-top: 20px;
  text-align: center;
}

.el-table {
  margin-top: 20px;
}

.el-dialog__body {
  padding: 20px;
}

.dialog-footer {
  text-align: right;
}
</style>
